// shadcn/ui Button (new-york), vendored per components.json with rtl: true —
// physical utilities are written as logical ones so the layout mirrors under
// dir="rtl" with no component changes.
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

// NFR-06 — "every button … must grow rather than clip": the base classes are
// `inline-flex` + padding with no width, no overflow control and no
// truncation, so a translation longer than the English widens the button
// instead of being scissored. `nfr06-grow-not-clip.test.tsx` holds the
// perimeter — no call site may hand a button a clipping class.
//
// NFR-38's / GEN-184's visible-focus half lives in these base classes: every button in
// the product draws the `focus-visible` ring (inputs carry their own in
// input.tsx), and keyboard REACH is NFR-36's real-button/real-link rule —
// together they are why every screen can be walked with Tab in DOM order with
// the focused control always visible.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        /*
         * THE DESTRUCTIVE INK IS THEME-AWARE, AND THE `dark:` HALF IS LOAD-BEARING.
         *
         * `--destructive` is a DARK red in the light theme and a LIGHT one in the
         * dark theme, so a single ink cannot serve both: white measures 4.77:1 on
         * the light fill and 2.89:1 on the dark one — every Delete, Cancel and
         * confirmation-box action button in the dark theme, under the 4.5 bar.
         *
         * The FILL is what carries the meaning here — NFR-14 makes red the colour
         * of an act that destroys — so the fill does not move and the ink does.
         * That is the same repair delegated ruling D20 made to the base button's
         * dark ink, for the same reason: in dark the fill is the light surface, so
         * the ink is the half with room to go darker.
         *
         * `slate-800` is the FIRST step down Tailwind's slate ladder — the shadcn
         * baseColor every neutral in this product comes from — that clears 4.5 on
         * that fill, at 5.06:1. `slate-700`, the step above it, measures 3.59 and
         * does not, so there is no cheaper step to take. It is also the value
         * `.dark`'s --secondary and --muted already carry, so this adds no new
         * colour to the product, only a second use of one it already has.
         *
         * `src/test/theme-contrast.test.ts` READS THIS STRING rather than assuming
         * white, and pins both halves. Deleting the `dark:` half does not fail to
         * compile — Tailwind simply falls back to `text-white`, which is exactly
         * what that file would then measure — so the ratchet is what catches it.
         */
        destructive: 'bg-destructive text-white dark:text-slate-800 hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
